import { motion } from 'framer-motion';
import { ExternalLink, GitFork, User } from 'lucide-react';

interface ProjectCardProps {
  id: string;
  name: string;
  subdomain: string;
  url: string;
  description: string | null;
  agentName: string;
  authorName?: string | null;
  authorHandle?: string | null;
  forkCount: number;
  index: number;
}

export default function ProjectCard({
  name,
  url,
  description,
  agentName,
  authorName,
  authorHandle,
  forkCount,
  index,
}: ProjectCardProps) {
  const displayAuthor = authorHandle ? `@${authorHandle}` : authorName || agentName;
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      className="group glass-panel rounded-2xl overflow-hidden shadow-lg hover:shadow-xl hover:bg-white/10 transition-all duration-300"
    >
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <div className="aspect-[16/10] bg-gray-900 relative overflow-hidden">
          <iframe
            src={url}
            title={name}
            className="absolute inset-0 w-full h-full border-0 pointer-events-none scale-[0.5] origin-top-left opacity-90 transition-opacity group-hover:opacity-100"
            style={{ width: '200%', height: '200%' }}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent group-hover:from-black/10 transition-colors" />
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0 duration-300">
            <div className="bg-black/80 backdrop-blur-sm rounded-full p-2 shadow-lg border border-white/10">
              <ExternalLink className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>
      </a>

      <div className="p-5">
        <h3 className="font-semibold text-white mb-1 truncate">{name}</h3>
        <p className="text-sm text-gray-400 mb-3 line-clamp-2 min-h-[2.5rem]">
          {description || 'No description'}
        </p>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5 text-gray-500">
            <User className="w-4 h-4" />
            <span className="truncate max-w-[140px]">{displayAuthor}</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-500">
            <GitFork className="w-4 h-4" />
            <span>{forkCount}</span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
